/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Auth0 authentication provider
 *
 * Adapts the Auth0 Angular SDK to the provider-neutral authentication contract.
 * This confines Auth0-specific options, data shapes, and Siemens logout routing
 * to the authentication boundary.
 */

import {inject, Injectable} from '@angular/core';
import {Auth0ClientService, AuthService} from '@auth0/auth0-angular';
import {filter, firstValueFrom, map, Observable, startWith} from 'rxjs';

import {environment} from '../../../environments/environment';
import type {
  IAuthenticatedUser,
  IAuthenticationError,
  IAuthenticationLoginOptions,
  IAuthenticationLogoutOptions,
  IAuthenticationProvider,
} from '../authentication-provider.token';
import {
  DEFAULT_LOGOUT_RETURN_URL,
  resolveRelativeReturnUrl,
  resolveReturnUrl,
} from '../return-url.util';

/**
 * Auth0 implementation of the provider-neutral webapp authentication contract.
 */
@Injectable({providedIn: 'root'})
export class Auth0AuthenticationProviderService implements IAuthenticationProvider {
  private readonly authService = inject(AuthService);
  private readonly auth0Client = inject(Auth0ClientService);

  readonly isAuthenticated$ = this.authService.isAuthenticated$;
  readonly user$: Observable<IAuthenticatedUser | null> =
    this.authService.user$.pipe(
      map((user) =>
        user?.sub
          ? {
              sub: user.sub,
              ...(user.name ? {name: user.name} : {}),
              ...(user.email ? {email: user.email} : {}),
            }
          : null,
      ),
    );
  /**
   * Latest normalized authentication error.
   *
   * The Auth0 SDK already publishes token retrieval and redirect failures on
   * its own `error$`, so that stream is the single error source of this adapter.
   */
  readonly error$: Observable<IAuthenticationError | null> =
    this.authService.error$.pipe(
      map((error) => this.toAuthenticationError(error)),
      startWith(null),
    );

  /**
   * Gets an Auth0 access token after the SDK completes its startup flow, and
   * optionally bypasses the SDK token cache.
   *
   * Waiting for Auth0 startup preserves the former AuthHttpInterceptor
   * behavior while a redirect callback or initial session check is pending.
   * Failures are published by the SDK on `error$` (for example with the code
   * `login_required` when no session exists) and rethrown to the caller.
   *
   * @param forceRefresh - Whether to require a fresh token from Auth0.
   * @returns A promise resolving to a valid Auth0 access token.
   */
  async getAccessToken(forceRefresh = false): Promise<string> {
    await firstValueFrom(
      this.authService.isLoading$.pipe(filter((isLoading) => !isLoading)),
    );

    return firstValueFrom(
      this.authService.getAccessTokenSilently(
        forceRefresh ? {cacheMode: 'off'} : undefined,
      ),
    );
  }

  /**
   * Starts an Auth0 login flow, optionally requesting Auth0's signup screen.
   *
   * @param options - Login intent and optional post-login route.
   * @returns A promise that resolves after Auth0 accepts the redirect.
   */
  async login(options?: IAuthenticationLoginOptions): Promise<void> {
    const authorizationParams = options?.registration
      ? {
          display: 'page' as const,
          prompt: 'login' as const,
          screen_hint: 'signup' as const,
        }
      : undefined;
    const appState = options?.returnUrl
      ? {target: resolveRelativeReturnUrl(options.returnUrl)}
      : undefined;

    await firstValueFrom(
      this.authService.loginWithRedirect({
        ...(authorizationParams ? {authorizationParams} : {}),
        ...(appState ? {appState} : {}),
      }),
    );
  }

  /**
   * Ends the Auth0 session using the existing Siemens federated logout route.
   *
   * @param options - Optional application-relative post-logout route.
   * @returns A promise that resolves after the browser logout navigation begins.
   */
  async logout(options?: IAuthenticationLogoutOptions): Promise<void> {
    await this.rotateRefreshTokenBeforeLogout();

    const loginPage = resolveReturnUrl(
      options?.returnUrl,
      DEFAULT_LOGOUT_RETURN_URL,
    );
    const auth0LogoutUrl = `https://${environment.auth0.domain}/v2/logout?client_id=${environment.auth0.clientId}&returnTo=${loginPage}`;
    const siemensLogoutUrl = `https://${environment.auth0.mainTenant}/v2/logout?returnTo=${encodeURIComponent(auth0LogoutUrl)}`;

    window.open(siemensLogoutUrl, '_self');
  }

  /**
   * Forces a refresh-token exchange immediately before the logout redirect.
   *
   * Security finding BL-3677: exchanging the refresh token right before the
   * Auth0 session is terminated rotates the refresh token family, so the
   * refresh token the browser held can no longer mint new access tokens after
   * logout. Already issued access tokens are not revoked by this exchange and
   * still expire naturally.
   *
   * The exchange goes through the Auth0 client directly instead of
   * `AuthService.getAccessTokenSilently()` so that an expected failure (for
   * example an already expired session) is neither published on `error$` nor
   * treated as an application-level authentication error. Logout always
   * continues regardless of the outcome.
   *
   * @returns A promise that resolves once the exchange attempt has finished.
   */
  private async rotateRefreshTokenBeforeLogout(): Promise<void> {
    try {
      await this.auth0Client.getTokenSilently({cacheMode: 'off'});
    } catch {
      // Logout must proceed even when silent authentication fails.
    }
  }

  /**
   * Normalizes an Auth0 SDK error for provider-neutral application UI.
   *
   * @param error - The Auth0 error emitted by the SDK.
   * @returns A normalized error containing the most useful Auth0 description.
   */
  private toAuthenticationError(error: unknown): IAuthenticationError {
    const errorWithDescription = error as Error & {
      error_description?: string;
      error?: string;
    };

    return {
      message:
        errorWithDescription.error_description ??
        errorWithDescription.message ??
        'Unknown error',
      ...(errorWithDescription.error ? {code: errorWithDescription.error} : {}),
    };
  }
}
